import type { FC, ReactNode } from 'react'

import { BedtimeCalculator } from '@/components/widgets/bedtime-calculator'
import { BreathingGuide } from '@/components/widgets/breathing-guide'
import { CaffeineCalculator } from '@/components/widgets/caffeine-calculator'
import { ClockChangePlanner } from '@/components/widgets/clock-change-planner'
import { HabitPlanner } from '@/components/widgets/habit-planner'
import { HeartRateZones } from '@/components/widgets/heart-rate-zones'
import { JetLagPlanner } from '@/components/widgets/jet-lag-planner'
import { MealPlanner } from '@/components/widgets/meal-planner'
import { MeditationTimer } from '@/components/widgets/meditation-timer'
import { RestQuiz } from '@/components/widgets/rest-quiz'
import { SocialJetlagCalculator } from '@/components/widgets/social-jetlag-calculator'
import { StretchingRoutineBuilder } from '@/components/widgets/stretching-routine-builder'
import { WaterCalculator } from '@/components/widgets/water-calculator'
import { WorkingWeekCalculator } from '@/components/widgets/working-week-calculator'
import { WorkoutPlanner } from '@/components/widgets/workout-planner'

interface ArticleBodyProps {
    content: string
}

// Interactive widgets an article body can embed. A body opts in with an HTML
// comment, e.g. `<!--widget:bedtime-calculator-->`, which survives the rich-text
// editor untouched and renders as nothing if the widget is ever removed.
const WIDGETS: Record<string, FC> = {
    'bedtime-calculator': BedtimeCalculator,
    'breathing-guide': BreathingGuide,
    'caffeine-calculator': CaffeineCalculator,
    'clock-change-planner': ClockChangePlanner,
    'habit-planner': HabitPlanner,
    'heart-rate-zones': HeartRateZones,
    'jet-lag-planner': JetLagPlanner,
    'meal-planner': MealPlanner,
    'meditation-timer': MeditationTimer,
    'rest-quiz': RestQuiz,
    'social-jetlag-calculator': SocialJetlagCalculator,
    'stretching-routine-builder': StretchingRoutineBuilder,
    'water-calculator': WaterCalculator,
    'working-week-calculator': WorkingWeekCalculator,
    'workout-planner': WorkoutPlanner
}

// The capture group means `split` returns alternating segments:
// even indices are HTML, odd indices are widget names.
const WIDGET_PATTERN = /<!--\s*widget:([a-z0-9-]+)\s*-->/gi

const LEADING_WIDGET = /^\s*<!--\s*widget:([a-z0-9-]+)\s*-->/i

export const isWidgetMarker = (value: string): boolean => LEADING_WIDGET.test(value)

// Tool pages render their widget straight under the H1, so the page template
// lifts the opening marker out of the body and renders the rest as usual.
export const splitLeadingWidget = (content: string): { widget: string | null; rest: string } => {
    const match = LEADING_WIDGET.exec(content)
    if (!match) return { widget: null, rest: content }

    return { widget: match[1].toLowerCase(), rest: content.slice(match[0].length) }
}

interface ArticleWidgetProps {
    name: string
}

export const ArticleWidget: FC<ArticleWidgetProps> = ({ name }): ReactNode => {
    const Widget = WIDGETS[name.toLowerCase()]
    return Widget ? <Widget /> : null
}

// Post bodies are stored as HTML (authored in the admin rich-text editor / CMS).
// The `.article-body` styles in globals.css style the raw HTML elements.
export const ArticleBody: FC<ArticleBodyProps> = ({ content }): ReactNode => {
    const segments = content.split(WIDGET_PATTERN)

    if (segments.length === 1) {
        return <div className='article-body' dangerouslySetInnerHTML={{ __html: content }} />
    }

    return (
        <div className='article-body'>
            {segments.map((segment, index) => {
                if (index % 2 === 1) {
                    const Widget = WIDGETS[segment.toLowerCase()]
                    return Widget ? <Widget key={`widget-${index}`} /> : null
                }

                if (!segment.trim()) return null

                return (
                    <div
                        key={`html-${index}`}
                        dangerouslySetInnerHTML={{ __html: segment }}
                    />
                )
            })}
        </div>
    )
}
